import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useMsal } from '@azure/msal-react';
import 'primereact/resources/themes/saga-blue/theme.css'; // Import the theme
import 'primereact/resources/primereact.min.css'; // Core CSS
import 'primeicons/primeicons.css'; // Icons
import 'primeflex/primeflex.css'; // PrimeFlex CSS
import './ShippingViews.css'; // Shared CSS

const CalibrationDevices = ({ userRole }) => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(100); // Number of items per page
  const [totalItems, setTotalItems] = useState(0); // Total items count
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [searchQuery, setSearchQuery] = useState(''); // Search query state

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();

  useEffect(() => {
    if (accounts.length > 0) {
      fetchData(page, limit, sortField, sortOrder, searchQuery);
    }
  }, [accounts, page, limit, sortField, sortOrder]);

  const fetchData = async (page, limit, sortField, sortOrder, searchQuery) => {
    setLoading(true);
    try {
      console.log('Attempting to acquire token...');
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: ["https://database.windows.net/.default"],
        account: accounts[0]
      });
      console.log('Token acquired:', tokenResponse.accessToken);

      await fetchDataWithToken(tokenResponse.accessToken, page, limit, sortField, sortOrder, searchQuery);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response && error.response.data.message === 'Token expired, please refresh your token.') {
        console.log('Token expired, refreshing...');
        try {
          const tokenResponse = await instance.acquireTokenSilent({
            scopes: ["https://database.windows.net/.default"],
            account: accounts[0]
          });
          await fetchDataWithToken(tokenResponse.accessToken, page, limit, sortField, sortOrder, searchQuery);
        } catch (refreshError) {
          console.error('Error refreshing token:', refreshError);
        }
      } else {
        console.error('Unexpected error:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchDataWithToken = async (token, page, limit, sortField, sortOrder, searchQuery) => {
    try {
      const response = await axios.get(`${apiBaseUrl}/api/calibration-devices`, {
        params: {
          page: page + 1,
          limit,
          sortField,
          sortOrder,
          searchQuery, // Pass the search query as a parameter
        },
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      console.log('API response:', response);

      const fetchedData = response.data.items || [];
      setData(fetchedData); // Set data
      setTotalItems(response.data.totalItems || 0); // Ensure totalItems is a number
      setFilteredData(fetchedData); // Initialize filtered data
    } catch (error) {
      console.error('Error fetching data with token:', error);
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
        console.error('Response headers:', error.response.headers);
      }
    }
  };

  const onPageChange = (event) => {
    setPage(event.page);
    setLimit(event.rows);
  };

  const onSort = (event) => {
    setSortField(event.sortField);
    setSortOrder(event.sortOrder);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleSearch = () => {
    fetchData(page, limit, sortField, sortOrder, searchQuery);
  };

  const clearSearch = () => {
    setSearchQuery('');
    fetchData(page, limit, sortField, sortOrder, '');
  };

  const viewItem = (serialNumber) => {
    navigate(`/calibration-device/${serialNumber}`);
  };

  const editItem = (serialNumber) => {
    navigate(`/edit-calibration-device/${serialNumber}`);
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <div className="action-buttons">
        <button onClick={() => viewItem(rowData.SerialNumber)} className="view-item-button button">
          View Device
        </button>
        {userRole === 'Admin' && (
          <button onClick={() => editItem(rowData.SerialNumber)} className="edit-item-button button">
            Edit Device
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="page-container">
      <div className="header-container">
        <Link to="/calibration">
          <button className="back-button">Back</button>
        </Link>
        <h1>Calibration Device Query</h1>
      </div>
      <div className="search-container">
        <input
          type="text"
          className="search-box"
          placeholder="Search..."
          value={searchQuery}
          onChange={handleSearchChange}
        />
        <button onClick={handleSearch} className="search-button">Search</button>
        <button onClick={clearSearch} className="clear-button">Clear</button>
      </div>
      <div className="table-container">
        <DataTable
          value={filteredData}
          paginator
          rows={limit}
          totalRecords={totalItems}
          loading={loading}
          lazy
          first={page * limit}
          onPage={onPageChange}
          sortField={sortField}
          sortOrder={sortOrder}
          onSort={onSort}
        >
          <Column field="Category" header="Category" sortable />
          <Column field="SerialNumber" header="Serial Number" sortable />
          <Column field="OwningLocation" header="Owning Location" sortable />
          <Column field="Manufacturer" header="Manufacturer" sortable />
          <Column field="ModelNumber" header="Model Number" sortable />
          <Column field="SESNumber" header="SES Number" sortable />
          <Column body={actionBodyTemplate} header="Actions" />
        </DataTable>
      </div>
    </div>
  );
};

export default CalibrationDevices;
